import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Gavel,
  MapPin,
  Bell,
  Info,
  Car,
  Globe,
  Users,
} from "lucide-react";
import SingleAuction from "./singleAuction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locals/translations.json";
import axios from "axios";
import usePageMeta from "../hooks/usePageMeta.js";
import { useNavigate } from "react-router-dom";
import placeholder from "../assets/Car-place-holder-PNG.png";
import skyautologo from "../assets/skyAutologo.png";
import { applyStandardPdfStyles } from "../utils/pdfUtils";
import html2pdf from "html2pdf.js";
import api from "../lib/api.ts";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

// The location mapUrl may be a full Google "<iframe ...>" embed snippet or a bare
// URL. Return the actual URL to use as an iframe `src` (extract src= if present).
const extractMapSrc = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  const match = trimmed.match(/src\s*=\s*["']([^"']+)["']/i);
  return match ? match[1] : trimmed;
};

const Auctions = () => {
  usePageMeta("auctions");
  const auctionInfoRef = useRef(null);
  const navigate = useNavigate();

  const { language, translate } = useLanguage();
  const [currentDate, setCurrentDate] = useState(dayjs().tz(DUBAI_TZ));
  const [events, setEvents] = useState([]);
  const [todayAuction, setTodayAuction] = useState(null);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const [liveAuction, setLiveAuction] = useState([]);
  const [AuctionLaneStatus, setAuctionLaneStatus] = useState([]);
  const [viewMode, setViewMode] = useState("monthly"); // "monthly", "weekly", "list"

  const toDubaiDateStr = (dateStr) => {
    if (!dateStr) return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    try {
      return dayjs(dateStr).tz(DUBAI_TZ).format("YYYY-MM-DD");
    } catch (error) {
      console.warn("Error formatting date:", dateStr, error);
      return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    }
  };

  // Fetch auctions
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const res2 = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              page: 1,
              limit: 50,
              orderBy: "startDate",
              order: "ASC",
              search: "",
              slug: "auctions",
              type: "live",
              filters: {
                unSoldVehicles: 1,
              },
            },
          },
        );

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              type: "upcomming,live",
              orderBy: "startDate",
              order: "ASC",
              filters: {
                isActive: true,
                unSoldVehicles: 1,
              },
            },
          },
        );

        const auctions = res.data.data;
        setLiveAuction(auctions);

        const laneStatusMap = {};
        auctions.forEach((auction) => {
          if (auction.auctionLanes) {
            laneStatusMap[auction.id] = auction.auctionLanes.map((lane) => ({
              laneId: lane.id || lane.laneId,
              status:
                lane.auctionStatus?.name ||
                lane.auctionStatus?.slug ||
                lane.status,
            }));
          }
        });
        setAuctionLaneStatus(laneStatusMap);

        const mapped = auctions
          .map((a) => {
            if (!a || typeof a !== "object") return null;

            let auctionName = `Auction ${a.id}`;
            if (typeof a.name === "string" && a.name.trim())
              auctionName = a.name.trim();
            else if (typeof a.auction === "string" && a.auction.trim())
              auctionName = a.auction.trim();

            const auctionLanes =
              a.auctionLanes
                ?.map((lane, idx) => {
                  const vehicles = lane.auctionLaneVehicles || [];
                  const laneName = lane.lane?.name || lane.name;
                  const mappedStatus = AuctionLaneStatus[a.id]?.find(
                    (status) => status.laneId === (lane.id || lane.laneId),
                  )?.status;

                  return {
                    id: lane.id || idx,
                    name:
                      typeof laneName === "string"
                        ? laneName
                        : `Lane ${idx + 1}`,
                    vehicles,
                    status: mappedStatus || "unknown",
                    totalVehicles: Array.isArray(vehicles)
                      ? vehicles.length
                      : 0,
                  };
                })
                .filter((lane) => lane !== null) || [];

            const totalCars = auctionLanes.reduce(
              (sum, lane) => sum + (lane.totalVehicles || 0),
              0,
            );

            return {
              id: a.id,
              auction: auctionName,
              name: a.name,
              date: toDubaiDateStr(a.startDate),
              startDate: a.startDate,
              branch: a.branch,
              status: a.status,
              location: a.location,
              type: a.type,
              description: a.description,
              auctionLanes,
              totalCars,
            };
          })
          .filter((a) => a !== null && a.totalCars > 0);

        setEvents(mapped);

        // Find next auction logic
        const now = dayjs().tz(DUBAI_TZ);
        let nextAuction = null;
        let minDiff = Infinity;

        mapped.forEach((a) => {
          if (!a.startDate) return;
          const auctionTime = dayjs.utc(a.startDate).tz(DUBAI_TZ);
          const diff = auctionTime.diff(now);
          if (diff >= 0 && diff < minDiff) {
            minDiff = diff;
            nextAuction = a;
          } else if (
            nextAuction === null &&
            diff < 0 &&
            Math.abs(diff) < minDiff
          ) {
            minDiff = Math.abs(diff);
            nextAuction = a;
          }
        });

        if (!selectedAuction && auctions.length > 0)
          setSelectedAuction(auctions[0]);
        else setSelectedAuction(nextAuction);
      } catch (err) {
        console.error("Error fetching auctions", err);
      }
    };

    fetchAuctions();
  }, []);

  // Calendar weeks (for monthly view)
  const weeks = useMemo(() => {
    const year = currentDate.year();
    const month = currentDate.month();

    const startOfMonth = dayjs.tz(`${year}-${month + 1}-01`, DUBAI_TZ);
    const endOfMonth = startOfMonth.endOf("month");
    const startDay = startOfMonth.day(); // 0 (Sun) to 6 (Sat)
    const daysInMonth = endOfMonth.date();

    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null); // Empty slots for start padding

    for (let d = 1; d <= daysInMonth; d++) {
      days.push(dayjs.tz(`${year}-${month + 1}-${d}`, DUBAI_TZ));
    }

    const weeksArr = [];
    for (let i = 0; i < days.length; i += 7)
      weeksArr.push(days.slice(i, i + 7));

    // Fill last week with empty slots if needed
    const lastWeek = weeksArr[weeksArr.length - 1];
    if (lastWeek.length < 7) {
      for (let i = lastWeek.length; i < 7; i++) lastWeek.push(null);
    }

    return weeksArr;
  }, [currentDate]);

  // Weekly view - show current week
  const currentWeek = useMemo(() => {
    const startOfWeek = currentDate.startOf("week"); // Sunday
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, "day"));
    }
    return days;
  }, [currentDate]);

  // Navigation for weekly view
  const prevWeek = () => {
    setCurrentDate(currentDate.subtract(1, "week"));
  };

  const nextWeek = () => {
    setCurrentDate(currentDate.add(1, "week"));
  };

  const prevMonth = () => setCurrentDate((prev) => prev.subtract(1, "month"));
  const nextMonth = () => setCurrentDate((prev) => prev.add(1, "month"));

  // Group auctions by date
  const auctionsByDate = useMemo(() => {
    const grouped = {};
    events.forEach((auction) => {
      if (auction && auction.date) {
        if (!grouped[auction.date]) grouped[auction.date] = [];
        grouped[auction.date].push(auction);
      }
    });
    return grouped;
  }, [events]);

  // Handle mouse enter for popup
  const handleMouseEnter = (event, dateStr) => {
    const auctionsOnDate = auctionsByDate[dateStr];
    if (auctionsOnDate && auctionsOnDate.length > 1) {
      const rect = event.currentTarget.getBoundingClientRect();
      setPopupPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
      setHoveredDate(dateStr);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    if (!isPopupHovered) {
      setHoveredDate(null);
    }
  };

  // Handle click outside popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hoveredDate &&
        !event.target.closest(".auction-popup") &&
        !isPopupHovered
      ) {
        setHoveredDate(null);
      }
    };

    if (hoveredDate) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [hoveredDate, isPopupHovered]);

  const activeAuction =
    selectedAuction ||
    (liveAuction.length > 0 ? liveAuction[0] : null) ||
    todayAuction;

  const handleViewCatalog = (auction, lane = null) => {
    console.log("handleViewCatalog called with auction:", auction, "lane:", lane);
    const laneId = lane?.id || lane?.laneId || lane?.lane?.id;
    const prefix = `/vehicles?auctionId=${auction.id}`;
    const laneQuery = laneId ? `&laneId=${laneId}` : "";
    console.log("Navigating to:", `${prefix}${laneQuery}`);
    navigate(`${prefix}${laneQuery}`);
  };

  const handleJoinAuction = (auction, lane = null) => {
    const laneId =
      lane?.id ||
      lane?.laneId ||
      lane?.lane?.id ||
      (auction?.auctionLanes && auction.auctionLanes[0]
        ? auction.auctionLanes[0]?.id || auction.auctionLanes[0]?.laneId
        : null);

         const auctionName = auction.location?.name || auction.branch?.name;
         console.log("Auction Name in Auction",auctionName);

    if (laneId) {
        navigate(`/livebid/${auction.id}/${laneId}`, {
      state: {
        auctionName,
      },
    });
  } else {
    navigate(`/vehicles?auctionId=${auction.id}`, {
      state: {
        auctionName,
      },
    });
  }
};

  const handleDownloadCatalog = async (auction, lane = null) => {
    try {
      const vehicles = [];

      const extractLaneVehicles = (laneItem) => {
        const laneVehiclesData =
          laneItem?.auctionLaneVehicles || laneItem?.vehicles || [];

        return laneVehiclesData.map((lv) => {
          if (lv?.vehicle) {
            return {
              ...lv,
              ...lv.vehicle,
              vehicleId: lv.vehicleId,
              currentBid: lv.currentBid,
              runNo: lv.runNo,
            };
          }
          return lv;
        });
      };

      const lanesToProcess = lane
        ? [lane]
        : auction?.auctionLanes && auction.auctionLanes.length > 0
          ? auction.auctionLanes
          : [];

      if (lanesToProcess.length > 0) {
        for (const laneItem of lanesToProcess) {
          vehicles.push(...extractLaneVehicles(laneItem));
        }
      } else {
        // no lanes: maybe direct vehicles array in auction object
        vehicles.push(...extractLaneVehicles(auction));
      }

      if (vehicles.length === 0) {
        alert("No vehicles found for this auction/lane.");
        return;
      }

      // Load image via proxy (backend) or fallback to placeholder
      const loadImage = async (url) => {
        if (!url) return placeholder;
        try {
          const response = await api.get("/products/image-proxy", {
            params: { url },
            responseType: "blob",
          });
          return URL.createObjectURL(response.data);
        } catch {
          return placeholder;
        }
      };

      // Load all vehicle images
      const vehiclesWithSrc = await Promise.all(
        vehicles.map(async (v) => ({
          ...v,
          imgSrc: await loadImage(v.images?.[0]?.imageUrl),
        })),
      );

      // Generate PDF
      const generatePDF = () => {
        const auctionDate = dayjs(auction.startDate).format("MMMM D, YYYY");
        const auctionTime = dayjs(auction.startDate).format("hh:mm A");
        const location =
          auction.location?.name || auction.branch?.name || "Online Only";
        const generatedDate = dayjs().format("MMMM D, YYYY [at] hh:mm A");
        const laneLabel =
          lane?.name || lane?.lane?.name || lane?.id || lane?.laneId || "All";

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 30px; color: #2d3436; line-height: 1.5; background: #fff; }
  .header { display:flex; align-items:center; padding-bottom:15px; border-bottom:3px solid #cad0d4; }
  .logo-section img { height:65px; }
  .header-info-right { display:flex; flex-direction:column; align-items:flex-start; margin-left:50px;}
  .auction-name { 
    font-size: 30px; 
    font-weight: 800; 
    color: #135EA0; 
    display: inline-block;
    line-height: 1;
    padding-bottom: 10px; 
    margin: 0;
    vertical-align: middle;
  }
  .auction-meta { font-size:13px; font-weight:600; color:#666; }
  
  .lane-info { 
    padding: 6px 15px; 
    border-radius: 6px; 
    font-weight: 700; 
    color: #135EA0; 
    display: inline-block; 
    margin-top: 5px;
    margin-bottom: 15px; 
    font-size: 12px; 
    line-height: 1;
  }

  table { width:100%; border-collapse:collapse; table-layout:fixed; background: transparent; }
  
  thead th { 
    background: #99A3AC; 
    color: white; 
    padding: 10px 0 22px 0; /* ASYMMETRIC PADDING: counteracts html2canvas downward shift */
    font-size: 11px; 
    text-align: center;
    vertical-align: middle;
    line-height: normal;
  }
  
  tbody td { 
    border-bottom:1px solid #eee; 
    padding: 8px 8px 18px 8px; /* Already uses the same strategy */
    font-size:11px; 
    text-align:center !important; 
    vertical-align: middle; 
  }

  /* EXCEPTION: No extra bottom padding for the image cell */
  tbody td.col-img { 
    padding-bottom: 8px !important; 
  }
  
  tr:nth-child(even) { background: rgba(253, 253, 253, 0.8); }
  tr { page-break-inside: avoid; text-align: center; }
  
  .col-img { width:50px; }
  .col-lot { width:60px; font-weight:700; }
  .col-vehicle { width:180px; font-weight:600; }
  .col-run { width:60px; font-weight:700; }
  .col-mile { width:80px; }
  .col-bid { width:90px; font-weight:700; }
  .col-cond { width:80px; }

  .cell-img { width:40px; height:30px object-fit:cover; display: block; margin: 0 auto; }
</style>
</head>
<body>
  <div class="header">
    <div class="logo-section"><img src="${skyautologo}" /></div>
    <div class="header-info-right">
      <h1 class="auction-name">${auction.name}</h1>
      <div class="auction-meta">${location} | ${auctionDate} | ${auctionTime}</div>
    </div>
  </div>
  <div class="lane-info">${laneLabel} | INVENTORY - ${vehiclesWithSrc.length} Vehicles</div>
  <table>
    <thead>
      <tr>
        <th class="col-img">IMG</th>
        <th class="col-lot">LOT #</th>
        <th class="col-vehicle">VEHICLE</th>
        <th class="col-run">RUN</th>
        <th class="col-mile">MILEAGE</th>
        <th class="col-bid">START BID</th>
        <th class="col-cond">COND.</th>
      </tr>
    </thead>
    <tbody>
      ${vehiclesWithSrc
        .map(
          (v) => `
      <tr>
        <td class="col-img"><img src="${v.imgSrc || placeholder}" class="cell-img" /></td>
        <td class="col-lot">${v.lotNumber || "—"}</td>
        <td class="col-vehicle">${v.year || ""} ${v.name || ""}</td>
        <td class="col-run">${v.runNumber || v.year || "-"}</td>
        <td class="col-mile">${Number(v.mileage || 0).toLocaleString()} mi</td>
        <td class="col-bid">$${Number(v.startingBid || 0).toLocaleString()}</td>
        <td class="col-cond">${v.Highlight?.name || "Clean"}</td>
      </tr>`,
        )
        .join("")}
    </tbody>
  </table>
</body>
</html>
`;

        const element = document.createElement("div");
        element.innerHTML = htmlContent;

        const safeAuctionName = (auction.name || "auction")
          .replace(/[^a-zA-Z0-9-_ ]/g, "")
          .replace(/\s+/g, "-");
        const safeLaneName = (laneLabel || "all")
          .replace(/[^a-zA-Z0-9-_ ]/g, "")
          .replace(/\s+/g, "-");

        const opt = {
          margin: [10, 10, 20, 10],
          filename: `${safeAuctionName}-${safeLaneName}-catalog.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        };

        html2pdf()
          .set(opt)
          .from(element)
          .toPdf()
          .get("pdf")
          .then((pdf) => {
            const pageCount = pdf.internal.getNumberOfPages();
            const pageWidth = pdf.internal.pageSize.width;
            const pageHeight = pdf.internal.pageSize.height;

            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i);
              // --- APPLY STANDARD STYLES (ARCS & FOOTER) ---
              applyStandardPdfStyles(pdf, generatedDate);
            }
          })
          .save();
      };

      generatePDF();
    } catch (error) {
      console.error("Error generating catalog PDF:", error);
      alert("Failed to generate catalog. Please try again.");
    }
  };

  const statsData = [
    { number: "142", label: "Auctions This Month", icon: Gavel },
    { number: "8.4k", label: "Total Vehicles", icon: Car },
    { number: "12", label: "Global Locations", icon: Globe },
    { number: "150k", label: "Active Buyers", icon: Users },
  ];

  return (
    <>
      <div className="w-full bg-primary border-b-2 border-gray px-6 py-4 mb-8 min-h-[120px] ">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col gap-3">
            {/* Main Title */}
            <h1>
              <span className="sm:text-xl lg:text-2xl font-bold text-white whitespace-nowrap overflow-hidden text-ellipsis">
                {translations[language]?.auctionCalendar || "Auction Calendar"}
              </span>
            </h1>

            {/* Description */}
            <p className="text-description text-[10px] sm:text-[14px] font-normal">
              {translations[language]?.auctionCalendarDesc ||
                "Stay ahead of the run. Browse and schedule upcoming live and timed auctions."}
            </p>
          </div>
        </div>
      </div>
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        {/* <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary dark:text-white mb-2">
          {translations[language]?.auctionCalendar || "Auction Calendar"}
        </h1>
        <p className="text-primary/80 text-lg">
          {translations[language]?.auctionCalendarDesc || "Stay ahead of the run. Browse and schedule upcoming live and timed auctions."}
        </p>

      </div> */}
        {/* --- PROFESSIONAL AUCTION HEADER --- */}

        {/* Filters & Toolbar */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 p-1 bg-[#1e293b] rounded-lg">
            <button
              onClick={() => setViewMode("monthly")}
              className={`px-6 py-2 rounded font-medium text-sm transition-all ${
                viewMode === "monthly"
                  ? "bg-primary text-white shadow-lg shadow-primary/20"
                  : "text-slate-400 hover:text-primary"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setViewMode("weekly")}
              className={`px-6 py-2 rounded font-medium text-sm transition-all ${
                viewMode === "weekly"
                  ? "bg-primary text-white shadow-lg shadow-primary/20"
                  : "text-slate-400 hover:text-primary"
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-6 py-2 rounded font-medium text-sm transition-all ${
                viewMode === "list"
                  ? "bg-primary text-white shadow-lg shadow-primary/20"
                  : "text-slate-400 hover:text-primary"
              }`}
            >
              List View
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3"></div>
        </div>

        {/* Main Content Grid */}
        {viewMode === "list" ? (
          // List View - Full Width
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white mb-4">
              All Upcoming Auctions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...liveAuction]
                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                .map((auction) => (
                  <div
                  key={auction.id}
                  className="bg-[#1e293b] border border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <span
                        className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${
                          auction.type === "live"
                            ? "bg-green-500/10 text-green-500"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {auction.type === "live"
                          ? "Live Auction"
                          : "Timed Event"}
                      </span>
                      <span className="text-slate-400 text-xs font-medium">
                        {dayjs(auction.startDate).format("MMM D, YYYY")}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold text-white mb-1">
                      {auction.auction || auction.name}
                    </h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      {dayjs(auction.startDate).format("hh:mm A")}
                    </p>
                    {auction.location?.name && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-1">
                        <MapPin size={14} />{" "}
                        {auction.location?.name || "Online Only"}
                      </p>
                    )}

                     {auction.description && (
                          <div className="mb-4 p-3 bg-primary/5 border border-primary/10 text-justify rounded-lg">
                            <p className="text-xs text-primary font-medium flex items-center text-slate-300 gap-2">
                              {auction.description}
                            </p>
                          </div>
                        )}

                    {/* <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => handleJoinAuction(auction)}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-2.5 rounded text-sm transition-colors"
                        title="Join live auction"
                      >
                        Join Auction
                      </button>
                      <button
                        onClick={() => handleViewCatalog(auction)}
                        className="w-full border border-gray-700 hover:bg-slate-800 text-white font-bold py-2.5 rounded text-sm transition-colors flex items-center justify-center gap-2"
                        title="View vehicle list"
                      >
                        <Car size={14} /> View List
                      </button>
                      <button
                        onClick={() => handleDownloadCatalog(auction)}
                        className="w-full border border-gray-700 hover:bg-slate-800 text-white font-bold py-2.5 rounded text-sm transition-colors flex items-center justify-center gap-2"
                        title="Download PDF catalog"
                      >
                        <Bell size={14} /> Download
                      </button>
                    </div> */}
                    {auction.auctionLanes && auction.auctionLanes.length > 0 ? (
                      <div className="space-y-2">
                        {auction.auctionLanes.map((lane) => {
                          const laneName =
                            lane?.name ||
                            lane?.lane?.name ||
                            `Lane ${lane?.id || lane?.laneId}`;

                          return (
                            <div
                              key={lane.id || lane.laneId || laneName}
                              className="p-2 bg-[#112133] border border-gray-700 rounded-lg"
                            >
                              <div className="flex w-full gap-2">
                                <button
                                  onClick={() =>
                                    handleJoinAuction(auction, lane)
                                  }
                                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-2 py-1.5 rounded text-xs"
                                >
                                  Join {laneName}
                                </button>

                                <button
                                  onClick={() =>
                                    handleViewCatalog(auction, lane)
                                  }
                                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                                >
                                  <Car size={14} />
                                  View List
                                </button>

                                <button
                                  onClick={() =>
                                    handleDownloadCatalog(auction, lane)
                                  }
                                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                                >
                                  <Bell size={14} />
                                  Download
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-2 bg-[#112133] border border-gray-700 rounded-lg">
                        <div className="flex w-full gap-2">
                          <button
                            onClick={() => handleJoinAuction(auction)}
                            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-2 py-1.5 rounded text-xs"
                          >
                            Join 
                          </button>

                          <button
                            onClick={() => handleViewCatalog(auction)}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                          >
                            <Car size={14} />
                            View List
                          </button>

                          <button
                            onClick={() => handleDownloadCatalog(auction)}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                          >
                            <Bell size={14} />
                            Download
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Calendar View (8 Columns) - Monthly or Weekly */}
            <div className="lg:col-span-8 bg-[#1e293b] rounded-xl border border-gray-700 overflow-hidden">
              {/* Calendar Header */}
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">
                  {viewMode === "weekly"
                    ? `Week of ${currentWeek[0].format("MMM D")} - ${currentWeek[6].format("MMM D, YYYY")}`
                    : currentDate.format("MMMM YYYY")}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={viewMode === "weekly" ? prevWeek : prevMonth}
                    className="p-2 hover:bg-primary/10 rounded-lg transition-colors border border-gray-700 text-slate-300"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={viewMode === "weekly" ? nextWeek : nextMonth}
                    className="p-2 hover:bg-primary/10 rounded-lg transition-colors border border-gray-700 text-slate-300"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              {/* Calendar Weekdays */}
              <div className="grid grid-cols-7 bg-[#111a21] border-b border-gray-700">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div
                      key={day}
                      className="py-3 text-center text-xs font-bold text-slate-400 uppercase"
                    >
                      {day}
                    </div>
                  ),
                )}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {(viewMode === "weekly" ? [currentWeek] : weeks).map(
                  (week, wIdx) =>
                    week.map((day, dIdx) => {
                      const isPlaceholder = !day;
                      if (isPlaceholder)
                        return (
                          <div
                            key={`empty-${wIdx}-${dIdx}`}
                            className={`${viewMode === "weekly" ? "min-h-[300px]" : "min-h-[120px]"} p-2 border-r border-b border-gray-700 bg-slate-800/30`}
                          ></div>
                        );

                      const dateStr = day.format("YYYY-MM-DD");
                      const dayEvents = auctionsByDate[dateStr] || [];
                      const isSelected =
                        selectedAuction &&
                        dayEvents.some((e) => e.id === selectedAuction.id);
                      const isToday = day.isSame(dayjs().tz(DUBAI_TZ), "day");

                      return (
                        <div
                          key={dateStr}
                          onClick={() => {
                            if (dayEvents.length > 0)
                              setSelectedAuction(dayEvents[0]);
                          }}
                          onMouseEnter={(e) => handleMouseEnter(e, dateStr)}
                          onMouseLeave={handleMouseLeave}
                          className={`${viewMode === "weekly" ? "min-h-[300px]" : "min-h-[120px]"} p-2 border-r border-b border-gray-700 cursor-pointer transition-colors relative bg-[#1e293b]
                      ${isSelected ? "bg-primary/5 ring-1 ring-inset ring-primary/30" : "hover:bg-slate-800/20"}
                      ${isToday ? "bg-primary/5" : ""}
                    `}
                        >
                          <div className="flex flex-col h-full">
                            <span
                              className={`text-sm font-medium ${isToday ? "text-primary font-bold" : "text-slate-300"}`}
                            >
                              {viewMode === "weekly"
                                ? day.format("ddd D")
                                : day.date()}
                            </span>

                            {/* Event Indicators */}
                            <div className="mt-2 space-y-1 flex-grow">
                              {dayEvents.length > 0 &&
                                (viewMode === "weekly" ? (
                                  // Weekly view shows more details
                                  dayEvents.map((event, idx) => (
                                    <div
                                      key={idx}
                                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold ${
                                        event.type === "live"
                                          ? "bg-green-500/10 text-green-500"
                                          : "bg-primary/10 text-primary"
                                      }`}
                                    >
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full ${event.type === "live" ? "bg-green-500" : "bg-primary"}`}
                                      ></span>
                                      <span className="truncate">
                                        {event.name}
                                      </span>
                                    </div>
                                  ))
                                ) : // Monthly view shows summary
                                dayEvents.length === 1 ? (
                                  <div
                                    className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold ${
                                      dayEvents[0].type === "live"
                                        ? "bg-green-500/10 text-green-500"
                                        : "bg-primary/10 text-primary"
                                    }`}
                                    title={dayEvents[0].name}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full ${dayEvents[0].type === "live" ? "bg-green-500" : "bg-primary"}`}
                                    ></span>
                                    {/* {dayEvents[0].type === 'live' ? 'LIVE' : 'TIMED'} */}
                                    <span className="block">
                                      {dayEvents[0].name
                                        ? dayEvents[0].name.length > 15
                                          ? dayEvents[0].name.slice(0, 15) +
                                            "..."
                                          : dayEvents[0].name
                                        : "Auction"}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 bg-primary text-white px-2 py-0.5 rounded text-[10px] font-bold">
                                    <Gavel size={10} /> {dayEvents.length}{" "}
                                    AUCTIONS
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      );
                    }),
                )}
              </div>
            </div>

            {/* Sidebar List View (4 Columns) */}
            <div className="lg:col-span-4 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {selectedAuction
                    ? `Auctions for ${dayjs(selectedAuction.startDate).format("MMM D")}`
                    : "Selected Auction"}
                </h3>
                {selectedAuction && (
                  <span className="text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded uppercase">
                    Selected
                  </span>
                )}
              </div>

              {/* Selected Auction Card(s) - Showing list for the selected day */}
              {selectedAuction ? (
                (auctionsByDate[selectedAuction.date] || [selectedAuction]).map(
                  (auction) => (
                    <div
                      key={auction.id}
                      className="bg-[#1e293b] border border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <span
                            className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${
                              auction.type === "live"
                                ? "bg-green-500/10 text-green-500"
                                : "bg-primary/10 text-primary"
                            }`}
                          >
                            {auction.type === "live"
                              ? "Live Auction"
                              : "Timed Event"}
                          </span>
                          <span className="text-slate-400 text-xs font-medium">
                            Starts {dayjs(auction.startDate).format("hh:mm A")}
                          </span>
                        </div>
                        <h4 className="text-lg font-bold text-white mb-1">
                          {auction.auction || auction.name}
                        </h4>
                        {auction.location?.name && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-1">
                            <MapPin size={14} />{" "}
                            {auction.location?.name || "Online Only"}
                          </p>
                        )}

                        {auction.description && (
                          <div className="mb-4 p-3 bg-primary/5 border border-primary/10 rounded-lg">
                            <p className="text-xs text-white font-medium text-justify flex items-center gap-2">
                              {auction.description}
                            </p>
                          </div>
                        )}

                        {auction.auctionLanes &&
                        auction.auctionLanes.length > 0 ? (
                          <div className="space-y-2">
                            {auction.auctionLanes.map((lane) => {
                              const laneName =
                                lane?.name ||
                                lane?.lane?.name ||
                                `Lane ${lane?.id || lane?.laneId}`;

                              return (
                                <div
                                  key={lane.id || lane.laneId || laneName}
                                  className="p-3 bg-[#112133] border border-gray-700 rounded-lg"
                                >
                                  <div className="flex w-full gap-2">
                                    <button
                                      onClick={() =>
                                        handleJoinAuction(auction, lane)
                                      }
                                      className="flex-1 w-full bg-primary hover:bg-primary/90 text-white font-semibold px-3 py-1.5 rounded text-xs"
                                    >
                                      Join {laneName}
                                    </button>

                                    <button
                                      onClick={() =>
                                        handleViewCatalog(auction, lane)
                                      }
                                      className="flex-1   bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                                    >
                                      <Car size={14} />
                                      View List
                                    </button>

                                    <button
                                      onClick={() =>
                                        handleDownloadCatalog(auction, lane)
                                      }
                                      className="flex-1 w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                                    >
                                      <Bell size={14} />
                                      Download
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-2 bg-[#112133] border border-gray-700 rounded-lg">
                        <div className="flex w-full gap-2">
                          <button
                            onClick={() => handleJoinAuction(auction)}
                            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-2 py-1.5 rounded text-xs"
                          >
                            Join 
                          </button>

                          <button
                            onClick={() => handleViewCatalog(auction)}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                          >
                            <Car size={14} />
                            View List
                          </button>

                          <button
                            onClick={() => handleDownloadCatalog(auction)}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-2 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                          >
                            <Bell size={14} />
                            Download
                          </button>
                        </div>
                      </div>
                        )}
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="bg-[#1e293b] border border-gray-700 rounded-xl p-8 text-center text-slate-400">
                  Select a date to view auctions
                </div>
              )}

              {/* Location Map Preview */}
              <div className="bg-[#1e293b] border border-gray-700 rounded-xl p-4">
                <h5 className="text-sm font-bold mb-3 uppercase tracking-wider text-slate-500">
                  Upcoming Regional Map
                </h5>
                <div className="relative h-48 rounded-lg overflow-hidden bg-slate-800 border border-gray-700 group">
                  {/* Fallback Map Image */}
                  {/* <div
                    className="absolute inset-0 bg-cover bg-center opacity-70 grayscale"
                    style={{
                      backgroundImage:
                        "url('https://lh3.googleusercontent.com/aida-public/AB6AXuDeGurP072jld9k5wMlhd1O9k2Un17-Jsd1RpqNc37gwUby8m1EAhVqVxDMpaFt2i9mepc_jNKcwp86J9v-of4A98zXlFrrZulerNkaeu3fMy6X9mixHs8dwxiPrHp-y-bDnSMOpUWRkRa99D9sAiC-h9Ob-YL4gzN8qwdgFxhIaao2ux29Y9OIzPszi0Ofg6RkAR1M_0LvI9B5dmip_dbjb8Qj90A_kJHSapP_scSs6HBbb_u8uVYdIX-utKij-beB8ZTlBh5_7aU')",
                    }}
                  ></div> */}
                  <div className="absolute inset-0 opacity-70 grayscale">
                    <iframe
                      src={
                        extractMapSrc(selectedAuction?.location?.mapUrl) ||
                        "https://www.google.com/maps?q=Iraq&output=embed"
                      }
                      className="w-full h-full border-0"
                      loading="lazy"
                      // referrerPolicy="no-referrer-when-downgrade"
                       referrerPolicy="no-referrer"
                    ></iframe>
                  </div>
                  <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none">
                    <div className="w-6 h-6 bg-primary rounded-full border-2 border-white animate-pulse"></div>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-[#1e293b]/90 px-2 py-1 rounded text-[10px] font-bold text-white shadow-lg">
                    {selectedAuction?.location?.name}
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
                <div className="flex gap-3">
                  <Info className="text-primary flex-shrink-0" size={24} />
                  <div>
                    <h5 className="text-sm font-bold text-primary mb-1">
                      New To Auctions?
                    </h5>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Join our daily 'Auction Basics' webinars every Monday at
                      10 AM EST to learn the ropes of bidding and vehicle
                      logistics.
                    </p>
                    <a
                      className="text-xs font-bold text-primary hover:underline mt-2 inline-block uppercase tracking-wider transition-colors"
                      href="#"
                    >
                      Register Now →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multiple Auctions Popup */}
        {hoveredDate && auctionsByDate[hoveredDate] && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.2 }}
            className="auction-popup fixed z-50 bg-[#1e293b] rounded-lg shadow-xl border border-gray-700 p-4 min-w-[280px] max-w-[400px] w-auto"
            style={{
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
              transform: "translateX(-50%) translateY(-100%)",
            }}
            onMouseEnter={() => setIsPopupHovered(true)}
            onMouseLeave={() => {
              setIsPopupHovered(false);
              setTimeout(() => {
                setHoveredDate(null);
              }, 150);
            }}
          >
            {/* Arrow pointing down */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#1e293b]"></div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div className="text-center flex-1">
                  <h4 className="font-semibold text-white text-sm">
                    {dayjs(hoveredDate).format("MMMM D, YYYY")}
                  </h4>
                  <p className="text-xs text-slate-400">
                    {auctionsByDate[hoveredDate].length}{" "}
                    {translations[language]?.auctions || "Auctions"}
                  </p>
                </div>
                <button
                  onClick={() => setHoveredDate(null)}
                  className="ml-2 p-1 hover:bg-slate-700 rounded-full transition-colors"
                  title={translations[language]?.close || "Close"}
                >
                  <svg
                    className="w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div
                className={`space-y-2 ${
                  auctionsByDate[hoveredDate].length > 4
                    ? "max-h-64 overflow-y-auto"
                    : ""
                }`}
              >
                {auctionsByDate[hoveredDate].map((auction, index) => (
                  <motion.div
                    key={auction.id || index}
                    className={`p-3 rounded-md cursor-pointer transition-all duration-200 ${
                      selectedAuction?.id === auction.id
                        ? "bg-primary text-white"
                        : "bg-slate-700 hover:bg-slate-600 hover:shadow-md"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedAuction(auction);
                      setHoveredDate(null);
                      auctionInfoRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium break-words ${
                            selectedAuction?.id === auction.id
                              ? "text-white"
                              : "text-white"
                          }`}
                        >
                          {auction.auction || auction.name}
                        </p>
                        <p
                          className={`text-xs ${
                            selectedAuction?.id === auction.id
                              ? "text-gray-200"
                              : "text-slate-400"
                          }`}
                        >
                          {dayjs
                            .utc(auction.startDate)
                            .tz(DUBAI_TZ)
                            .format("hh:mm A")}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
        <div className="mt-12">
          {/* Heading above the top border */}
          <div className="mb-4">
            <h2 className="text-white text-2xl uppercase font-bold">
              {translate("auction.ourTrust")}
            </h2>
          </div>

          {/* Stats grid with top border */}
          <div className="border-t border-gray-700 pt-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {statsData.map((stat, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1e293b] rounded-full flex items-center justify-center border border-gray-700">
                    <stat.icon className="text-primary" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {stat.number}
                    </p>
                    <p className="text-xs text-slate-400 uppercase font-medium">
                      {stat.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Bottom Stats/Legend */}
        {/* <div className="mt-12 border-t border-gray-700 pt-8">
 
  <h1 className="text-white text-2xl uppercase font-bold mb-6">Our Trust</h1>

        
  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
    {statsData.map((stat, index) => (
      <div key={index} className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#1e293b] rounded-full flex items-center justify-center border border-gray-700">
          <stat.icon className="text-primary" size={24} />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{stat.number}</p>
          <p className="text-xs text-slate-400 uppercase font-medium">
            {stat.label}
          </p>
        </div>
      </div>
    ))}
  </div>
</div> */}
      </main>
    </>
  );
};

export default Auctions;
